/**
 * Central charging-analytics module — single source of truth for every EV
 * charging derivation on the frontend. Mirrors `/app/backend/charging_calc.py`
 * so the live-preview cost in the Log Energy dialog exactly matches what
 * the server persists.
 *
 * Anything computing:
 *   • battery energy added   (kWh from ΔSoC × usable capacity)
 *   • charging efficiency    (0–1 from charger_type × temperature_category)
 *   • grid energy consumed   (battery / efficiency)
 *   • charging loss          (grid − battery)
 *   • temperature category   (from date + user timezone hemisphere)
 * must call into this module. Do NOT re-implement these formulas inside
 * React components, forms, or hooks — import from here so the definition
 * remains DRY across the app.
 */

export const CHARGER_TYPES = ["level_1", "level_2", "dc_fast"];
export const TEMPERATURE_CATEGORIES = ["cold", "mild", "hot"];

export const DEFAULT_CHARGER_TYPE = "level_2";
export const DEFAULT_TEMPERATURE_CATEGORY = "mild";

/** Efficiency matrix keyed by `${temp}|${charger}`. Values = round-trip
 *  fraction of grid energy that lands in the traction battery. */
export const CHARGING_EFFICIENCY_MATRIX = Object.freeze({
  "cold|level_1": 0.70,
  "cold|level_2": 0.80,
  "cold|dc_fast": 0.75,
  "mild|level_1": 0.82,
  "mild|level_2": 0.88,
  "mild|dc_fast": 0.92,
  "hot|level_1": 0.78,
  "hot|level_2": 0.85,
  "hot|dc_fast": 0.80,
});

const SOUTHERN_TZ_PREFIXES = [
  "Australia/",
  "Pacific/Auckland",
  "Pacific/Chatham",
  "Pacific/Fiji",
  "Pacific/Norfolk",
  "Pacific/Efate",
  "Pacific/Guadalcanal",
  "Pacific/Noumea",
  "Pacific/Port_Moresby",
  "America/Argentina/",
  "America/Sao_Paulo",
  "America/Santiago",
  "America/Montevideo",
  "America/Asuncion",
  "America/La_Paz",
  "America/Lima",
  "Africa/Johannesburg",
  "Antarctica/",
  "Indian/Reunion",
  "Indian/Mauritius",
];

export function normalizeChargerType(value) {
  if (!value) return DEFAULT_CHARGER_TYPE;
  const v = String(value).trim().toLowerCase().replace(/[\s-]/g, "_");
  if (CHARGER_TYPES.includes(v)) return v;
  if (["l1", "120v", "120v_ac", "ac_l1", "trickle"].includes(v)) return "level_1";
  if (["l2", "240v", "240v_ac", "ac_l2", "home", "public_ac"].includes(v)) return "level_2";
  if (["dcfc", "dc", "fast", "supercharger", "chademo", "ccs"].includes(v)) return "dc_fast";
  return DEFAULT_CHARGER_TYPE;
}

export function normalizeTempCategory(value) {
  if (!value) return DEFAULT_TEMPERATURE_CATEGORY;
  const v = String(value).trim().toLowerCase();
  if (TEMPERATURE_CATEGORIES.includes(v)) return v;
  if (["cool", "freezing", "winter"].includes(v)) return "cold";
  if (["warm", "summer", "heat"].includes(v)) return "hot";
  if (["temperate", "moderate", "spring", "fall", "autumn"].includes(v)) return "mild";
  return DEFAULT_TEMPERATURE_CATEGORY;
}

/** kWh actually stored in the traction battery.
 *  Returns null when inputs are incomplete; 0 when Δ is non-positive. */
export function batteryEnergyAddedKwh(capacityKwh, startPct, endPct) {
  const cap = parseFloat(capacityKwh);
  const s = parseFloat(startPct);
  const e = parseFloat(endPct);
  if (!isFinite(cap) || cap <= 0 || !isFinite(s) || !isFinite(e)) return null;
  const delta = e - s;
  if (delta <= 0) return 0;
  return (delta / 100) * cap;
}

export function chargingEfficiency(chargerType, tempCategory) {
  const ct = normalizeChargerType(chargerType);
  const tc = normalizeTempCategory(tempCategory);
  return (
    CHARGING_EFFICIENCY_MATRIX[`${tc}|${ct}`] ??
    CHARGING_EFFICIENCY_MATRIX[`${DEFAULT_TEMPERATURE_CATEGORY}|${DEFAULT_CHARGER_TYPE}`]
  );
}

export function gridEnergyKwh(batteryEnergy, efficiency) {
  const b = parseFloat(batteryEnergy);
  const e = parseFloat(efficiency);
  if (!isFinite(b) || !isFinite(e) || e <= 0) return null;
  return b / e;
}

export function chargingLossKwh(grid, battery) {
  const g = parseFloat(grid);
  const b = parseFloat(battery);
  if (!isFinite(g) || !isFinite(b)) return null;
  return Math.max(0, g - b);
}

export function isSouthernHemisphere(timezoneStr) {
  if (!timezoneStr) return false;
  const tz = String(timezoneStr).trim();
  return SOUTHERN_TZ_PREFIXES.some((p) => tz.startsWith(p));
}

/** Infer temperature category from ISO date + user timezone. Missing /
 *  invalid inputs fall back to the spec-mandated "mild" default. */
export function deriveTempCategory(dateIso, timezoneStr) {
  if (!dateIso) return DEFAULT_TEMPERATURE_CATEGORY;
  const parts = String(dateIso).split("-");
  const month = parseInt(parts[1], 10);
  if (!isFinite(month) || month < 1 || month > 12) return DEFAULT_TEMPERATURE_CATEGORY;
  let cold = new Set([12, 1, 2]);
  let hot = new Set([6, 7, 8]);
  if (isSouthernHemisphere(timezoneStr)) [cold, hot] = [hot, cold];
  if (cold.has(month)) return "cold";
  if (hot.has(month)) return "hot";
  return "mild";
}

/**
 * Single entry point for every downstream consumer. Callers pass whatever
 * context they have; missing inputs are inferred (temp from date+tz,
 * charger from vehicle default, battery kWh from ΔSoC × capacity).
 * Returns an object with every derived metric, always safe to spread.
 *
 * Precedence:
 *   chargerType:  explicit → location pref → vehicle default → level_2
 *   tempCategory: explicit → derived (date + tz) → mild
 */
export function deriveChargeMetrics({
  batteryEnergyKwh,
  capacityKwh,
  startBatteryPct,
  endBatteryPct,
  chargerType,
  temperatureCategory,
  dateIso,
  timezone,
  locationChargerPref,
  vehicleDefaultCharger,
} = {}) {
  let battery = batteryEnergyKwh;
  if (battery == null || battery === "") {
    battery = batteryEnergyAddedKwh(capacityKwh, startBatteryPct, endBatteryPct);
  } else {
    const n = parseFloat(battery);
    battery = isFinite(n) ? n : null;
  }

  const resolvedCharger = normalizeChargerType(
    chargerType || locationChargerPref || vehicleDefaultCharger || DEFAULT_CHARGER_TYPE
  );
  const resolvedTemp = normalizeTempCategory(
    temperatureCategory || deriveTempCategory(dateIso, timezone)
  );
  const eff = chargingEfficiency(resolvedCharger, resolvedTemp);
  const grid = gridEnergyKwh(battery, eff);
  const loss = chargingLossKwh(grid, battery);

  return {
    battery_energy_kwh: battery,
    grid_energy_kwh: grid,
    charging_efficiency: eff,
    charging_loss_kwh: loss,
    charger_type: resolvedCharger,
    temperature_category: resolvedTemp,
  };
}
